import './RiskAssessment.css';
import React, { useState } from 'react';
import { locationsService } from '../services/locations';
import { Location } from '../types';
import { getLocationPath, sortLocationsByPath } from '../utils/locationPaths';

type LocationType = Location['type'];

interface CreateLocationInlineProps {
  facilityId: string;
  locations: Location[];
  onCreated: (location: Location) => void;
}

const LOCATION_TYPES: LocationType[] = ['building', 'wing', 'floor', 'zone', 'room'];

/** Compact reusable form for creating a location without leaving the current workflow. */
const CreateLocationInline: React.FC<CreateLocationInlineProps> = ({
  facilityId,
  locations,
  onCreated,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState<LocationType>('room');
  const [parentId, setParentId] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /** Validate and create the location, then notify the owning view. */
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError('Location name is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const created = await locationsService.createLocation({
        facility_id: facilityId,
        name: name.trim(),
        type,
        ...(parentId ? { parent_location_id: parentId } : {}),
      });
      onCreated(created);
      setName('');
      setParentId('');
      setType('room');
      setExpanded(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to create location');
    } finally {
      setSubmitting(false);
    }
  };

  if (!expanded) {
    return <button className="btn btn-secondary btn-sm" type="button" onClick={() => setExpanded(true)}>+ New location</button>;
  }

  return (
    <form className="create-location-inline" onSubmit={submit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <label style={{ fontSize: 12 }}>
        Location name
        <br />
        <input value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <label style={{ fontSize: 12 }}>
        Type
        <br />
        <select value={type} onChange={(event) => setType(event.target.value as LocationType)}>
          {LOCATION_TYPES.map((item) => <option key={item} value={item}>{item.replace(/^./, c => c.toUpperCase())}</option>)}
        </select>
      </label>
      <label style={{ fontSize: 12 }}>
        Parent location
        <br />
        <select value={parentId} onChange={(event) => setParentId(event.target.value)}>
          <option value="">— none —</option>
          {sortLocationsByPath(locations).map((location) => (
            <option key={location.id} value={location.id}>
              {getLocationPath(location, locations)}
            </option>
          ))}
        </select>
      </label>
      <button className="btn btn-secondary btn-sm" type="submit" disabled={submitting}>{submitting ? 'Creating…' : 'Create'}</button>
      <button className="btn btn-secondary btn-sm" type="button" onClick={() => setExpanded(false)}>Cancel</button>
      {error && <span role="alert" style={{ color: '#b91c1c' }}>{error}</span>}
    </form>
  );
};

export default CreateLocationInline;
